import { useEffect } from "react";

import { syncCreReportHtmlAttribute } from "../../../../utils/reportClipboard";

/**
 * CRE extension — report HTML bridge (page side).
 *
 * ## Why this exists
 * PiP **Copy report** runs in the extension content script. It cannot call TipTap
 * `editor.getHTML()` directly, so we mirror the latest report HTML on the DOM:
 *
 *   ReportEditorSection (`data-cre-report-root`)
 *     └── `data-cre-report-html`  ← cleaned HTML (no AI diff classes)
 *
 * The extension reads that attribute in `CREExtension/src/content/reportPlainText.ts`
 * and builds plain text for the clipboard (lists, tables, draggable paragraphs, etc.).
 *
 * ## Where to use
 * Call once from `Patient.jsx` next to `useCreDictationMirror` / the main RTE:
 *
 *   `useCreReportHtmlSync(editorRef, transcript);`
 *
 * Toolbar **Copy** does not depend on this attribute; it uses `reportClipboard.js` live.
 *
 * @param {React.RefObject} editorRef - RTE wrapper ref (`editorRef.current.editor` = TipTap).
 * @param {string} transcript - Controlled report HTML; triggers re-sync when the page sets
 *   content without a TipTap `update` event (templates, Process AI, draft restore, etc.).
 */
export function useCreReportHtmlSync(editorRef, transcript) {
  // TipTap may mount after Patient first render — poll until `editor.on` is available.
  useEffect(() => {
    let cancelled = false;
    let detach = () => {};

    const bindEditor = () => {
      const editor = editorRef.current?.editor;
      if (!editor?.on) {
        if (!cancelled) window.requestAnimationFrame(bindEditor);
        return;
      }
      const sync = () => syncCreReportHtmlAttribute(editor);
      sync();
      editor.on("update", sync);
      detach = () => editor.off("update", sync);
    };

    bindEditor();
    return () => {
      cancelled = true;
      detach();
    };
  }, [editorRef]);

  // External transcript changes (not always emitted as TipTap `update`).
  useEffect(() => {
    syncCreReportHtmlAttribute(editorRef.current?.editor);
  }, [editorRef, transcript]);
}
